<template>
  <DefineTemplate v-slot="{shortcut}">
    {{ shortcut.title }}
    <img v-if="shortcut.logo" :src="shortcut.logo" :alt="shortcut.title" class="max-w-14">
    <Icon v-if="shortcut.icon" :icon="shortcut.icon" class="text-primary" />
    <div
      v-if="shortcut.count && !!shortcut.count()"
      class="ml-2"
    >
      <span class="font-bold">{{ shortcut.count() }}</span>
    </div>
  </DefineTemplate>

  <div class="flex flex-col w-72 mt-6">
    <div class="sticky top-6 w-72">
      <h2 class="text-lg self-end pr-5">
        Accès rapides
      </h2>
      <div class="flex flex-col gap-3 mt-3">
        <div v-for="(shortcut, index) in shortcuts" :key="index">
          <GlowingContainer
            v-if="shortcut.highlight"
            class="highlighted pr-0 !rounded-r-none !rounded-l-full shadow-md cursor-pointer [&_.content]:!bg-secondary-lightest"
            @click="select(index)"
          >
            <ReuseTemplate :shortcut="shortcut" />
          </GlowingContainer>

          <div
            v-else
            class="flex items-center justify-between py-2 pl-4 pr-8 rounded-l-full shadow-md cursor-pointer transition-all duration-300 ease-in-out bg-white hover:bg-lightPurple"
            @click="select(index)"
          >
            <ReuseTemplate :shortcut="shortcut" />
          </div>
        </div>
      </div>
      <interview-short-cut-drawer-element
        :component-config="component"
        @close="component = null"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, markRaw, ref} from 'vue';
import {storeToRefs} from 'pinia';
import dayjs from 'dayjs';

import InterviewProfessionalAttachments
    from '@/interview/components/instance-modal/short-cuts/InterviewProfessionalAttachments.vue';
import InterviewShortCutDrawerElement from '@/interview/components/instance-modal/InterviewShortCutDrawerElement.vue';
import InterviewPatientAttachments
    from '@/interview/components/instance-modal/short-cuts/InterviewPatientAttachments.vue';
import InterviewDrugInteraction from '@/interview/components/instance-modal/short-cuts/InterviewDrugInteraction.vue';
import InterviewTranscript from '@/interview/components/instance-modal/InterviewTranscript.vue';
import InterviewSurveys from '@/interview/components/instance-modal/short-cuts/InterviewSurveys.vue';
import InterviewConsumerNotes from '@/interview/components/instance-modal/short-cuts/InterviewConsumerNotes.vue';
import InterviewVaccinationTimeline
    from '@/interview/components/instance-modal/short-cuts/InterviewVaccinationTimeline.vue';
import Icon from '@/core/components/icon/Icon.vue';
import InterviewInstanceSummary
    from '@/interview/components/instance-modal/short-cuts/InterviewInstanceSurveySummary.vue';
import {useInterviewStore} from '@/stores/interview-store';
import ConsumerKeyService from '@/consumer/services/ConsumerKeyService';
import Vidal from '@/assets/images/logos/logo-vidal.png';
import InterviewKnowledgeables from "@/interview/components/instance-modal/short-cuts/InterviewKnowledgeables.vue";
import GlowingContainer from "@/core/components/glowing-container/GlowingContainer.vue";
import {createReusableTemplate} from "@vueuse/core";

const component = ref<null | { component: Element; componentProps: Record<string, unknown>; minWidth: string; title: string; }>(null);

const interviewStore = useInterviewStore();
const {
    interview,
    loading,
    consumerKey,
    transcript,
    surveys,
    professionalAttachments,
    patientAttachments,
    noteCount,
    backing
} = storeToRefs(interviewStore);

const [DefineTemplate, ReuseTemplate] = createReusableTemplate();


const initialShortcut = ref([
    {
        slug: 'knowledge-box',
        title: 'Demander à l\'IA ',
        component: markRaw(InterviewKnowledgeables),
        minWidth: '50%',
        icon: 'mdi-creation',
    },
    {
        title: 'Bibliographie',
        component: markRaw(InterviewProfessionalAttachments),
        count: () => professionalAttachments.value?.length || 0,
        minWidth: '50%'
    },
    {
        title: 'Ressources patient',
        component: markRaw(InterviewPatientAttachments),
        count: () => patientAttachments.value?.length || 0,
        minWidth: '50%'
    },
    {
        title: 'Questionnaires',
        component: markRaw(InterviewSurveys),
        count: () => surveys.value?.length || 0,
        minWidth: '40%'
    },
    {
        title: 'Ordos & délivrances',
        slug: 'drug-interaction',
        component: markRaw(InterviewDrugInteraction),
        minWidth: '100%',
        logo: Vidal
    },
    {
        title: 'Notes internes',
        count: () => noteCount.value,
        component: markRaw(InterviewConsumerNotes)
    },
    {
        title: "Suivi vaccinal",
        component: markRaw(InterviewVaccinationTimeline),
        minWidth: 'auto'
    }
]);

const shortcuts = computed(() => {
    const shortcuts: any[] = [...initialShortcut.value];

    if (loading.value.transcript || transcript.value) {
        shortcuts.push({
            title: `Transcript${loading.value.transcript ? ' ...' : ''}`,
            component: InterviewTranscript,
            minWidth: '400px'
        });
    }

    const autoEvaluationStep = backing.value?.steps.find(step => step.auto_evaluation);
    if (backing.value && !!autoEvaluationStep?.completed_at) {
        const latestInstance = autoEvaluationStep.interview_instances?.sort((a, b) => {
            if (!a.ended_at) {
                return 1;
            }

            if (!b.ended_at) {
                return -1;
            }

            return dayjs(b.ended_at).unix() - dayjs(a.ended_at).unix();
        })[0] ?? null;

        if (latestInstance) {
            shortcuts.unshift({
                title: '✅ Auto-évaluation',
                component: markRaw(InterviewInstanceSummary),
                minWidth: '50%',
                highlight: true,
                componentProps: {
                    instance: latestInstance
                }
            });
        }
    }

    return shortcuts
        .filter((shortcut) => {
            switch (shortcut.slug) {
                case 'knowledge-box':
                    return (interview.value?.knowledgeables?.length || 0) > 0;
                case 'drug-interaction':
                    return !ConsumerKeyService.isBeneficiary(consumerKey.value ?? '');
                default:
                    return true;
            }
        })
        .map((shortcut) => {
            switch (shortcut.slug) {
                case 'knowledge-box':
                    return {
                        ...shortcut,
                        componentProps: {
                            knowledgeables: interview.value?.knowledgeables,
                        }
                    };
                default:
                    return shortcut;
            }
        });
});

const select = (index: number) => {
    component.value = {
        component: shortcuts.value[index].component ?? null,
        componentProps: shortcuts.value[index].componentProps ?? {},
        minWidth: shortcuts.value[index].minWidth ?? '300px',
        title: shortcuts.value[index].title ?? '',
    };
};

/**
 * Open the drug interaction shortcut.
 */
const openDrugsInteraction = () => {
    select(shortcuts.value.findIndex(shortcut => shortcut.slug === 'drug-interaction'));
};

defineExpose({
    openDrugsInteraction
});
</script>

<style scoped>
:deep(.highlighted .content) {
    @apply p-4 !rounded-r-none !rounded-l-full flex items-center justify-between py-2 pl-4 pr-8;
}
</style>
